import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Paragraph } from '@digdir/designsystemet-react';
import type { AdjustmentLogEntry, AssessedArea } from '../../../api/workEnvironmentTypes';
import { placementText } from '../assessmentView';
import { areaName, dayAndTime } from '../riskView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = { log: AdjustmentLogEntry[]; areas: AssessedArea[] };

/** Every move in the matrix, with who made it. The log is never rewritten. */
export const AdjustmentLog = ({ log, areas }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const nameOf = (factorKey: string) => {
    const area = areas.find((candidate) => candidate.key === factorKey);
    return area ? areaName(area, t) : factorKey;
  };
  return (
    <div className={classes.adjustmentLog}>
      <span className="ds-label" data-size="sm">
        {t('workEnvironmentRisk.assessment.log.title')}
      </span>
      <ul className={classes.adjustmentEntries}>
        {log.map((entry) => (
          <li key={`${entry.factorKey}-${entry.changedAt}`} className={classes.adjustmentEntry}>
            <span className="ds-label" data-size="sm">
              {nameOf(entry.factorKey)}
            </span>
            <span className="ds-paragraph" data-size="xs">
              {t('workEnvironmentRisk.assessment.log.before', { placement: placementText(entry.before, t) })}
            </span>
            <span className="ds-paragraph" data-size="xs">
              {t('workEnvironmentRisk.assessment.log.after', { placement: placementText(entry.after, t) })}
            </span>
            <span className="ds-paragraph" data-size="xs">
              {entry.reason
                ? t('workEnvironmentRisk.assessment.log.reason', { reason: entry.reason })
                : t('workEnvironmentRisk.assessment.log.backToCalculated')}
            </span>
            <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
              {[
                entry.changedBy &&
                  t('workEnvironmentRisk.activity.by', {
                    email: entry.changedBy,
                    role: t(`workEnvironmentRisk.activity.roles.${entry.changedByRole}`),
                  }),
                dayAndTime(entry.changedAt, t, i18n.language),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </li>
        ))}
      </ul>
      <Paragraph data-size="xs" className={process.subtle}>
        {t('workEnvironmentRisk.assessment.log.note')}
      </Paragraph>
    </div>
  );
};
